import { Grid } from "@mui/material";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import Button from "../../../framework/Button";
import GlobalContext from "../../../framework/GlobalContext";
import Shortcut from "../../../framework/Shortcut";
import TextInput from "../../../framework/TextInput";
import { displayToast } from "../../../framework/toast/toastUtils";
import { formStateHandler } from "../../../utils/utils";
import { transformPayload } from "../CacUserConstants";
import classes from "./index.module.css";
import { saveCACUser } from "../../../services/user-service";

const AddCacUser = ({
  onSaveCacUser = () => {},
  onEscape = () => {},
  cacUserData,
  isEdit = false,
}) => {
  const [formState, setFormState] = useState({});
  const [errorStates, setErrorStates] = useState({});
  const userNameRef = useRef();
  const passwordRef = useRef();
  const saveCacUser = useRef();
  const { setCurrentComponent } = useContext(GlobalContext);

  const validateForm = useCallback(async (fieldName, data) => {
    if (fieldName === "username" || !fieldName) {
      if (!data?.username || !data?.username?.trim()) {
        userNameRef.current?.focus();
        return displayToast({
          type: "error",
          title: "Please enter user name.",
        });
      }
    }
    if (fieldName === "password" || !fieldName) {
      if (data && !data?.password?.length) {
        displayToast({
          type: "error",
          title: "Please enter password.",
          autoHideDuration: 10000,
        });
        setErrorStates((prev) => {
          return { ...prev, password: true };
        });
        if (!fieldName) passwordRef.current.focus({ preventScroll: true });
        return false;
      }
      const regex =
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$#!%*?&]{8,}$/;
      if (!regex.test(data.password)) {
        displayToast({
          type: "error",
          title: (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                fontSize: "13px",
                fontWeight: 600,
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                Password must be alphanumeric.
              </div>
              <div style={{ display: "flex", alignItems: "center" }}>
                Password must be atleast 8 characters in length.
              </div>
              <div style={{ display: "flex", alignItems: "center" }}>
                Password must contain atleast one uppercase letter.
              </div>
              <div style={{ display: "flex", alignItems: "center" }}>
                Password must contain atleast one lowercase letter.
              </div>
              <div style={{ display: "flex", alignItems: "center" }}>
                Password must contain atleast one special character from
                (@$!%*?&).
              </div>
            </div>
          ),
        });
        setErrorStates((prev) => {
          return { ...prev, password: true };
        });
        if (!fieldName) passwordRef.current.focus({ preventScroll: true });
        return false;
      }
    }
    return true;
  }, []);
  const updateCacUserHandler = useCallback(
    async (e) => {
      if (!(await validateForm(null, formState))) return;
      const payload = transformPayload({ ...formState });
      return saveCACUser(payload).then((r) => {
        if (!r) return;
        onSaveCacUser(r);
        return displayToast({
          type: "success",
          title: "User updated successfully.",
        });
      });
    },
    [formState, onSaveCacUser, validateForm]
  );
  const keyupListener = useCallback(
    (event) => {
      const key = event.key?.toLowerCase();
      if (key === "escape") {
        return onEscape();
      }
      if (event.ctrlKey && key === "s") {
        return saveCacUser.current.click();
      }
    },
    [onEscape]
  );
  const handleFormChange = useCallback(
    (fieldName, fieldValue, isSelect = false, options, sendOption = false) => {
      const updatedFormState = formStateHandler(
        formState,
        fieldName,
        fieldValue,
        isSelect,
        options,
        sendOption
      );
      setFormState(updatedFormState);
    },
    [formState]
  );

  useEffect(() => {
    document.addEventListener("keyup", keyupListener, false);
    return () => {
      document.removeEventListener("keyup", keyupListener, false);
    };
  }, [keyupListener]);

  useEffect(() => {
    if (cacUserData) {
      setFormState({
        ...cacUserData,
      });
    }
  }, [cacUserData]);

  useEffect(() => {
    if (formState?._id) {
      passwordRef.current?.focus();
    } else {
      userNameRef.current?.focus();
    }
  }, [formState?._id]);
  return (
    <div>
      <h4 className={classes["cac-user-detail-label"]}>User Details</h4>
      <Grid container spacing={2} sx={{ p: "25px 20px" }}>
        <Grid item xs={12}>
          <TextInput
            id="add_user_userName"
            ref={userNameRef}
            name="userName"
            label="User Name"
            value={formState?.username || ""}
            onFocus={() => setCurrentComponent("add_user_userName")}
            onChange={(e) => {
              handleFormChange("username", e.target.value);
            }}
            InputProps={{
              readOnly: formState?._id,
            }}
            inputProps={{
              "data-markers": JSON.stringify({
                right: "add_user_password",
                down: "add_user_password",
                up: "add_user_userName",
                left: "add_user_userName",
              }),
            }}
            required={true}
          />
        </Grid>
        <Grid item xs={12}>
          <TextInput
            id="add_user_password"
            ref={passwordRef}
            type="password"
            name="password"
            label="Password"
            required={true}
            onFocus={() => setCurrentComponent("add_user_password")}
            onChange={(e) => {
              handleFormChange("password", e.target.value.replace(/\s/g, ""));
              if (errorStates?.password)
                setErrorStates((prev) => {
                  return { ...prev, password: false };
                });
            }}
            value={formState?.password || ""}
            onBlur={() => validateForm("password", formState)}
            maxInputLength={100}
            inputProps={{
              "data-markers": JSON.stringify({
                right: "add_cac_user_save_button",
                down: "add_cac_user_save_button",
                up: "add_user_userName",
                left: "add_user_userName",
              }),
            }}
            error={errorStates?.password}
          />
        </Grid>
      </Grid>
      <div className={classes["add-cac-user-save-button-wrapper"]}>
        <Button
          id="add_cac_user_save_button"
          ref={saveCacUser}
          endIcon={<Shortcut keys={["Ctrl", "S"]} />}
          label="Save User"
          color="violet"
          onClick={updateCacUserHandler}
          markers={JSON.stringify({
            up: "add_user_password",
            left: "add_user_password",
            right: "add_cac_user_save_button",
            down: "add_cac_user_save_button",
          })}
        />
      </div>
    </div>
  );
};

export default AddCacUser;
